/* eslint-disable no-custom-animation/no-custom-animation */

import type { CSSObject } from '@mantine/core';
import { keyframes } from '@mantine/core';

const TRANSITION_DURATIONS = {
	'duration-0': '0s',
	'duration-50': '0.05s',
	'duration-100': '0.1s',
	'duration-150': '0.15s',
	'duration-200': '0.2s',
	'duration-300': '0.3s',
	'duration-400': '0.4s',
	'duration-500': '0.5s',
	'duration-1000': '1s',
} as const;

export type TransitionDuration = keyof typeof TRANSITION_DURATIONS;

const TRANSITION_EASINGS = {
	/** Responds quickly and finishes with control. A great default for any user interaction */
	ease: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
	/** Starts slowly and finishes at top speed. Use sparingly. Use for elements that exit the screen. */
	easeIn: 'cubic-bezier(0.42, 0, 1, 1)',
	/** Starts at top speed and finishes slowly. Use sparingly. Use for elements that enter the screen. */
	easeOut: 'cubic-bezier(0.19, 0.91, 0.38, 1)',
	/** Starts and finishes with equal speed. A good default for transitions triggered by the system. */
	easeInOut: 'cubic-bezier(0.42, 0, 0.58, 1)',
	/** Moves with constant speed. Use for continuous and mechanical animations, such as rotating spinners. */
	linear: 'cubic-bezier(0, 0, 1, 1)',
} as const;

export type TransitionEasing = keyof typeof TRANSITION_EASINGS;

export type AnimationModifier =
	| 'backwards'
	| 'both'
	| 'forwards'
	| 'none'
	| 'infinite';

export function animation(
	keyframe: string,
	duration: TransitionDuration = 'duration-0',
	easing: TransitionEasing = 'ease',
	initialValues: CSSObject = {},
	modifiers: string = ''
): CSSObject {
	return {
		...initialValues,
		animation: `${keyframe} ${TRANSITION_DURATIONS[duration]} ${TRANSITION_EASINGS[easing]} ${modifiers}`,
	};
}

export function transition(
	duration: TransitionDuration = 'duration-0',
	easing: TransitionEasing = 'ease'
): (property?: string, modifiers?: string) => CSSObject {
	return (property: string = 'background-color', modifiers: string = '') => ({
		transition: `${property} ${TRANSITION_DURATIONS[duration]} ${TRANSITION_EASINGS[easing]} ${modifiers}`,
	});
}

const dropIn = keyframes({
	'0%': {
		opacity: 0,
		transform: 'translateY(-10px)',
	},
	'100%': {
		opacity: 1,
		transform: 'translateY(0)',
	},
});

const dropOut = keyframes({
	'0%': {
		opacity: 1,
		transform: 'translateY(0)',
	},
	'100%': {
		opacity: 0,
		transform: 'translateY(-10px)',
	},
});

export const buttonDropInAnimation = animation(
	dropIn,
	'duration-300',
	'ease',
	{ opacity: 0 },
	'forwards'
);

export const buttonDropOutAnimation = animation(
	dropOut,
	'duration-300',
	'ease',
	{ opacity: 1 },
	'forwards'
);

const blink = keyframes({
	'0%, 80%, 100%': {
		transform: 'scale(0)',
	},
	'40%': {
		transform: 'scale(1)',
	},
});

export const loaderBlinkAnimation = animation(
	blink,
	'duration-1000',
	'linear',
	{},
	'infinite both'
);

export const buttonHoverTransition = transition('duration-100', 'ease');

const pageFadeIn = keyframes({
	'0%': {
		opacity: 0,
	},
	'100%': {
		opacity: 1,
	},
});

export const pageFadeInAnimation = animation(
	pageFadeIn,
	'duration-100',
	'ease',
	{ opacity: 0 },
	'forwards'
);
